import { useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Group } from '@/core/api/entities/group'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useDeleteGroup } from '@/core/hooks/useGroup'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

interface UseGroupDeletionProps {
	onSuccess?: () => void
	onError?: (error: Error) => void
}

export const useGroupDeletion = (props?: UseGroupDeletionProps) => {
	const { onSuccess, onError } = props || {}
	const { showNotification } = useNotificationContext()
	const { t } = useTranslation('common')
	const queryClient = useQueryClient()
	const [selectedGroup, setSelectedGroup] = useState<Group | null>(null)
	const [isModalVisible, setIsModalVisible] = useState(false)

	const { mutate, isPending } = useDeleteGroup({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				if (selectedGroup?.id) {
					// Invalidate groups queries to refetch the list
					queryClient.invalidateQueries({ queryKey: ['groups'] })

					// Show success notification
					showNotification({
						type: 'success',
						message: t('entities.group.delete.success.title'),
						description: t('entities.group.delete.success.description', {
							name: selectedGroup.name,
						}),
					})

					// Close modal
					setIsModalVisible(false)
					setSelectedGroup(null)

					// Call onSuccess callback if provided
					if (onSuccess) onSuccess()
				}
			},
			onError: (error: Error) => {
				// Show error notification
				showNotification({
					type: 'error',
					message: t('entities.group.delete.error.title'),
					description: t('entities.group.delete.error.description', {
						name: selectedGroup?.name,
					}),
				})

				// Call onError callback if provided
				if (onError) onError(error)
			},
		},
	})

	const openDeleteModal = (group: Group) => {
		setSelectedGroup(group)
		setIsModalVisible(true)
	}

	const closeDeleteModal = () => {
		setIsModalVisible(false)
		setSelectedGroup(null)
	}

	const handleDeleteGroup = () => {
		if (selectedGroup?.id) {
			mutate(selectedGroup.id)
		}
	}

	return {
		selectedGroup,
		isModalVisible,
		isDeleting: isPending,
		openDeleteModal,
		closeDeleteModal,
		handleDeleteGroup,
	}
}
